import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { usePagedList, useDebouncedValue } from "@/lib/usePagedList";
import LoadMoreButton from "@/components/LoadMoreButton";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowLeft, Clock, BookOpen, Search as SearchIcon } from "lucide-react";
import { TechChip } from "@/lib/tech";
import { TagChip } from "@/lib/tags";
import LinkedKeywords from "@/components/LinkedKeywords";
import SmartImage from "@/components/SmartImage";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import Select from "@/components/Select";
import { buildHowToSchema, clampText } from "@/lib/schema";
import { TOPICS } from "@/lib/topics";
import { useRewrittenHtml } from "@/lib/useLinkPolicy";
import { articlePath } from "@/lib/radarPaths";
import { useArticle, articleSeoProps, useArticleLangRoutes } from "@/lib/useArticle";
import ArticleLangNote from "@/components/ArticleLangNote";

export const Tutorials = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const initialQ = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({ category: "all", technology: "all", since: "all", q: initialQ });

  useEffect(() => {
    api.get("/tutorials/technologies").then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  const dq = useDebouncedValue(filters.q);
  const { items, loading, loadingMore, hasMore, loadMore } = usePagedList("/tutorials", { ...filters, q: dq.trim(), language: lang });

  const allOpt = { value: "all", label: t.filters.all };
  const topicOpts = [allOpt, ...TOPICS.map((x) => ({ value: x.en, label: lang === "fr" ? x.fr : x.en }))];
  const techOpts = [allOpt, ...techs.map((v) => ({ value: v, label: v }))];
  const periodOpts = [
    { value: "all", label: t.filters.periodAll },
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="tutorials-page">
      <SeoHead
        lang={lang}
        path="/tutorials"
        title={t.tutorials.title}
        description={clampText(t.tutorials.subtitle, 160) || (lang === "fr"
          ? "Tutoriels Dataleo : guides pratiques pour construire et gouverner des outils IA en Supply Chain et Opérations."
          : "Dataleo tutorials: practical how-to guides for building and governing AI tools in Supply Chain and Operations.")}
      />
      <PageHero
        kicker={lang === "fr" ? "Pratique · Tutoriels" : "How-to · Tutorials"}
        title={t.tutorials.title}
        description={t.tutorials.subtitle}
        accent="yellow"
        testId="tutorials-hero"
      />
      <div className="dl-container py-10">

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex flex-col gap-1 text-[12px] flex-1 min-w-[220px]">
          <span className="dl-overline">{t.filters.search}</span>
          <div className="flex items-center border border-[#E5E7EB] bg-white">
            <SearchIcon size={16} className="ml-3 text-[#4B5563]" />
            <input
              data-testid="filter-search"
              type="text"
              placeholder={t.filters.search}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="flex-1 px-2 py-2 text-sm bg-transparent outline-none"
            />
          </div>
        </div>
        <Select testid="tutorial-filter-topic" label={t.filters.category} value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={topicOpts} />
        <Select testid="tutorial-filter-tech" label={lang === "fr" ? "Technologie" : "Technology"} value={filters.technology} onChange={(v) => setFilters({ ...filters, technology: v })} options={techOpts} />
        <Select testid="filter-period" label={t.filters.period} value={filters.since} onChange={(v) => setFilters({ ...filters, since: v })} options={periodOpts} />
        <button
          data-testid="filter-reset"
          onClick={() => setFilters({ category: "all", technology: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >
          {t.filters.reset}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tutorials-grid">
        {items.map((tu, i) => {
          const to = articlePath("tutorials", tu);
          return (
            <article
              key={tu.id}
              data-testid={`tutorial-card-${i}`}
              className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors flex flex-col overflow-hidden"
            >
              <Link to={to} className="aspect-[16/9] bg-[#F3F4F6] overflow-hidden block">
                <SmartImage
                  src={tu.featured_image}
                  entity={tu}
                  type="tutorial"
                  alt={tu.title || "Tutorial cover"}
                  className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
              </Link>
              <div className="p-4 flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-3 text-[10px] font-mono-dl tracking-wider uppercase text-[#4B5563]">
                  <span className="text-[#00A878]">{tu.difficulty}</span>
                  {tu.duration_minutes > 0 && (
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {tu.duration_minutes} {t.sections.minutes}</span>
                  )}
                  <span className="ml-auto text-[#9ca3af]">{tu.published_at}</span>
                </div>
                <h3 className="font-display text-lg leading-snug text-[#111827]">
                  <Link to={to} className="group-hover:text-[#00A878] transition-colors">{tu.title}</Link>
                </h3>
                {tu.subtitle && <p className="text-[13px] text-[#4B5563] line-clamp-2">{tu.subtitle}</p>}
                {(tu.technologies && tu.technologies.length > 0) && (
                  <div className="flex flex-wrap gap-1">
                    {tu.technologies.slice(0, 4).map((tech) => (
                      <TechChip key={tech} name={tech} variant="dark" size="xs" />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-auto pt-2">
                  {(tu.tags || []).slice(0, 3).map((tag) => (
                    <TagChip key={tag} value={tag} size="xs" />
                  ))}
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {!loading && <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={loadMore} lang={lang} testid="tutorials-load-more" />}
      </div>
    </div>
  );
};

export const TutorialDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { item, err } = useArticle("tutorials", id);
  useArticleLangRoutes(item);

  const sanitizedBody = useMemo(
    () => DOMPurify.sanitize(item?.body_html || "", {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["target", "rel", "allow", "allowfullscreen", "frameborder", "loading"],
    }),
    [item?.body_html],
  );
  const renderedBody = useRewrittenHtml(sanitizedBody);

  if (err) return <div className="dl-container py-20"><Link to="/radar/tutorials" className="text-sm">← {t.nav.tutorials}</Link><h1 className="font-display text-3xl mt-4">Not found</h1></div>;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]">…</div>;

  return (
    <article data-testid="tutorial-detail" className="dl-container py-10">
      <SeoHead
        lang={item.language || lang}
        {...articleSeoProps(item)}
        title={item.title}
        description={clampText(item.subtitle || item.body_html, 160) || "Dataleo tutorial - AI for Operations."}
        type="article"
        image={item.featured_image}
        publishedTime={item.published_at}
        modifiedTime={item.updated_at}
        jsonLd={buildHowToSchema({ baseUrl: SITE_URL, item, path: item.canonical_path })}
      />
      <div className="max-w-4xl">
        <Link to="/radar/tutorials" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
          <ArrowLeft size={14} /> {t.nav.tutorials}
        </Link>
        <ArticleLangNote item={item} lang={lang} />
        <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] font-mono-dl uppercase tracking-wider">
          <span className="px-2 py-0.5 bg-[#00A878] text-white inline-flex items-center gap-1"><BookOpen size={12} /> Tutorial</span>
          <span className="text-[#4B5563]">{item.difficulty}</span>
          {item.duration_minutes > 0 && <span className="inline-flex items-center gap-1 text-[#4B5563]"><Clock size={11} /> {item.duration_minutes} {t.sections.minutes}</span>}
          <span className="text-[#9ca3af]">{item.published_at}</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-[#111827]">{item.title}</h1>
        {item.subtitle && <p className="mt-4 text-[18px] text-[#4B5563] leading-relaxed">{item.subtitle}</p>}
        {(item.technologies && item.technologies.length > 0) && (
          <div data-testid="tutorial-tech-list" className="flex flex-wrap gap-1.5 mt-5">
            {item.technologies.map((tech) => (
              <TechChip key={tech} name={tech} variant="dark" />
            ))}
          </div>
        )}
        <div className="mt-8 border border-[#E5E7EB] overflow-hidden bg-[#F3F4F6]">
          <SmartImage src={item.featured_image} entity={item} type="tutorial" alt={item.title || "Tutorial cover"} className="w-full h-auto max-h-[480px]" />
        </div>
        <div
          className="tutorial-body mt-8 text-[16px] leading-[1.85] text-[#111827]"
          dangerouslySetInnerHTML={{ __html: renderedBody }}
        />
        {item.images && item.images.length > 0 && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {item.images.map((img, i) => (
              <div key={`${img}-${i}`} className="border border-[#E5E7EB] overflow-hidden bg-[#F3F4F6]">
                <SmartImage src={img} type="tutorial" alt={`${item.title || "Tutorial"} - figure ${i + 1}`} className="w-full h-auto" />
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-[#E5E7EB]">
          {(item.tags || []).map((tg) => (
            <TagChip key={tg} value={tg} />
          ))}
        </div>
        <LinkedKeywords keywords={item.linked_keywords} lang={lang} testid="tutorial-linked-keywords" />
        {item.url && (
          <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              data-testid="tutorial-read-source"
              className="inline-flex items-center gap-2 bg-[#111827] hover:bg-[#00A878] text-white px-5 py-2.5 text-sm font-mono-dl uppercase tracking-wider transition-colors"
            >
              {lang === "fr" ? "Lire la source" : "Read the source"}
              {item.source && <span className="text-[#9ca3af] normal-case font-sans">- {item.source}</span>}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        )}
      </div>
    </article>
  );
};

export default Tutorials;
